import type { ArchiveExpansion } from '~/utils/io/files/archive';
import type { FileTreeResult } from '~/utils/io/files/fileTree';
import type { ImportMessage } from './importMessage';
import type { ImportOutcome } from './importModel';

/** The wizard's shared wording: each function names a catalogue message. */

/** The instances the server confirmed storing across the attempts an entry recorded. */
export function landedInstanceCount(landed: readonly ImportOutcome[]): number {
    return landed.reduce((n, outcome) => n + outcome.instancesImported, 0);
}

/** A skipped entry that nevertheless sent files. */
function skippedWithLanded(landed: readonly ImportOutcome[]): ImportMessage {
    const count = landedInstanceCount(landed);
    return count > 0
        ? { id: 'patientList.import.skipped.withLanded', values: { count } }
        : { id: 'patientList.import.skipped.withLandedUnknownCount' };
}

/**
 * A skipped entry's wording. "No files sent" holds only when its commit landed nothing, and the
 * banner says so where the summary screen carries that reassurance on a separate line.
 *
 * `landingUnknown` says an attempt got no answer, so no landing evidence is not the same as
 * nothing sent; the reassurance is withheld rather than guessed at.
 */
export function skippedText(
    landed: readonly ImportOutcome[],
    placement: 'banner' | 'summary',
    landingUnknown = false
): ImportMessage {
    if (landed.length > 0) {
        return skippedWithLanded(landed);
    }
    if (landingUnknown) {
        return placement === 'banner'
            ? { id: 'patientList.import.skipped.landingUnknown' }
            : { id: 'patientList.import.skipped.landingUnknownBare' };
    }
    return placement === 'banner'
        ? { id: 'patientList.import.skipped.nothingSent' }
        : { id: 'patientList.import.skipped.bare' };
}

export function importedSummaryText(outcome: ImportOutcome): ImportMessage {
    return {
        id: outcome.wasNewPatient
            ? 'patientList.import.done.asNewPatient'
            : 'patientList.import.done.intoExisting',
    };
}

function megabytes(bytes: number): number {
    return Math.round(bytes / (1024 * 1024));
}

/** States what the refusal is about: the drop, not the queue. */
export function dropRefusalText(
    result: Exclude<FileTreeResult, { status: 'ok' }>
): ImportMessage {
    switch (result.status) {
        case 'too-many-bytes':
            return {
                id: 'patientList.import.refusal.tooLarge',
                values: { limitMb: megabytes(result.limitBytes) },
            };
        case 'unreadable':
            return {
                id: 'patientList.import.refusal.partiallyUnreadable',
                values: { count: result.count },
            };
    }
}

export function archiveRefusalText(
    result: Exclude<ArchiveExpansion, { status: 'ok' }>
): ImportMessage {
    switch (result.status) {
        case 'too-many-bytes':
            return {
                id: 'patientList.import.archive.tooLarge',
                values: { limitMb: megabytes(result.limitBytes) },
            };
        case 'too-deep':
            return {
                id: 'patientList.import.archive.tooDeep',
                values: { limit: result.limit },
            };
        case 'encrypted':
            return { id: 'patientList.import.archive.encrypted' };
        case 'unreadable':
            return { id: 'patientList.import.archive.unreadable' };
    }
}
